// =============================================================================
// io/sync/reviews.ts — the `reviews` table.
// -----------------------------------------------------------------------------
// One row per user per period. A real table with a per-row upsert, so D141's
// read-before-write hazard — which governs the jsonb collections — does not
// apply: two periods are two rows and neither can revert the other.
//
// ── 🚨 `reviews.id` IS A GLOBAL TEXT PRIMARY KEY, AND THAT IS WHY D179 HAPPENED ──
//
// The old client built the id from the CONTENT (`week-2026-08-09`), so the first
// user to save a week owned that id for the whole table and everybody else's
// upsert hit an RLS `USING` violation forever. The row is identified here by
// **cadence + period_start**, scoped to the caller by RLS, and a new row gets an
// id with real randomness in it. See `domain/review` and D179/D180.
//
// ── 🚨 THE SAVE RESOLVES WHETHER IT WORKED, AND THE CALLER MUST READ IT ──
//
// The same log that exposed D179 showed *"Review saved"* written in the SAME
// SECOND as *"Save failed (reviews-upsert)"* — beta's `.then(() => toast(…))`
// discarded the boolean underneath it and reported success over a refused write.
// So this returns a discriminated result and never a bare promise: a caller that
// ignores it does not type-check into a success toast.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Review, ReviewCadence } from '../../domain/review';
import { reportFailureAs } from '../diagnostics/errorLog';

const COLUMNS = 'id, cadence, period_start, period_end, answers, created_at, updated_at';

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * ⚠ Anything that is not a string is DROPPED rather than coerced. `answers` is a
 * free jsonb column: a legacy row could hold a number or a nested object under a
 * prompt key, and `String({})` in a textarea would render `[object Object]` and
 * then SAVE it back over whatever was really there.
 */
function answersFromDb(v: unknown): Record<string, string> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string') out[k] = val;
  }
  return out;
}

export function reviewFromDb(r: Record<string, unknown>): Review {
  // ⚠ Anything that is not one of the two enum values is read as a WEEK rather
  // than passed through. `cadence` is a Postgres enum, so a third value cannot
  // exist today — but a widened enum reaching a client that has never heard of
  // it must land somewhere defined, not in a period the math cannot compute.
  const cadence: ReviewCadence = r['cadence'] === 'month' ? 'month' : 'week';
  return {
    id: str(r['id']),
    cadence,
    periodStart: str(r['period_start']),
    periodEnd: str(r['period_end']),
    answers: answersFromDb(r['answers']),
    createdAt: typeof r['created_at'] === 'string' ? r['created_at'] : null,
    updatedAt: typeof r['updated_at'] === 'string' ? r['updated_at'] : null,
  };
}

/**
 * Every review the user has, newest period first.
 *
 * ⚠ NOT SCOPED TO A PERIOD, deliberately. The tab steps backwards through weeks
 * and months with two buttons, and a read per step would put a network round trip
 * behind every press of `‹ Prev week`. A daily journal is the collection that
 * grows; reviews accrue at 52 + 12 a year, so the whole set is small enough to
 * hold — Josh's book has five.
 *
 * ⚠ An empty list and a failed read are NOT the same thing. Every word on this
 * screen was typed by the user and exists nowhere else, so a failure that fell
 * through to a blank form would invite them to write over content that is merely
 * unreachable.
 */
export async function loadReviews(sb: SupabaseClient): Promise<Review[]> {
  const { data, error } = await sb
    .from('reviews')
    .select(COLUMNS)
    .order('period_start', { ascending: false });

  if (error) {
    throw new Error(
      `loadReviews: ${error.message}. Refusing to render an empty review form — `
      + `it would invite the trader to type over reflections that are merely unreachable.`,
    );
  }
  return ((data ?? []) as unknown as Record<string, unknown>[]).map(reviewFromDb);
}

export type SaveReviewResult =
  | { ok: true; review: Review }
  | { ok: false; error: string };

/**
 * Write one review, and hand back the row that was actually stored.
 *
 * ── 🚨 THE ID IS RESOLVED FROM THE PERIOD BEFORE THE WRITE ──
 *
 * The tab mints an id when it first shows an unsaved period. If the same period
 * was saved somewhere else in between — a second tab, another device — that row
 * already exists under a DIFFERENT id, and upserting ours would leave the user
 * with two reviews for one week and their earlier answers stranded in the one the
 * tab no longer looks at.
 *
 * ▶ So this asks the table which row covers this period first. RLS scopes that
 * query to the caller, so it can only ever find their own — which is the whole
 * shape D179 got wrong.
 *
 * ⚠ It resolves the ID ONLY. It does not read the stored answers and merge them:
 * that would be D141's hazard, where a re-read quietly reinstates text the trader
 * has just deleted. Last write wins on the content, as it does in beta.
 *
 * ⚠ `updated_at` IS NOT WRITTEN HERE. The table carries a `BEFORE UPDATE` trigger
 * that stamps it, and `created_at` defaults on insert — so the timestamps come
 * from the database rather than from whatever the browser's clock says.
 */
export async function saveReview(
  sb: SupabaseClient, userId: string, r: Review,
): Promise<SaveReviewResult> {
  if (!r.periodStart || !r.periodEnd) {
    return {
      ok: false,
      error: 'This review has no period — it cannot be stored without one.',
    };
  }

  const existing = await sb
    .from('reviews')
    .select('id')
    .eq('cadence', r.cadence)
    .eq('period_start', r.periodStart)
    .limit(1)
    .maybeSingle();

  // ⚠ Funnelled HERE rather than at the caller, because only this file can
  // tell a real failure from the validation refusal above — which is the user's
  // own incomplete form and must never be filed as a failure. See #52.
  if (existing.error) {
    return {
      ok: false,
      error: reportFailureAs('save review', existing.error, existing.error.message),
    };
  }

  const id = typeof existing.data?.id === 'string' ? existing.data.id : r.id;

  const { data, error } = await sb
    .from('reviews')
    .upsert({
      id,
      user_id: userId,
      cadence: r.cadence,
      period_start: r.periodStart,
      period_end: r.periodEnd,
      answers: r.answers,
    }, { onConflict: 'id' })
    .select(COLUMNS)
    .maybeSingle();

  if (error) {
    return { ok: false, error: reportFailureAs('save review', error, error.message) };
  }
  if (!data) {
    // ⚠ A write that returns no row is not a success with nothing to show — it is
    // a write whose result nobody can vouch for, and reporting it as saved is the
    // exact failure D179's second half was about.
    return { ok: false, error: 'The review was written but the database returned no row.' };
  }
  return { ok: true, review: reviewFromDb(data as unknown as Record<string, unknown>) };
}
